import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';

import { LanguageService } from '../../../../services/language.service';
import { BrandsService } from '../../../../services/brands.service';
import { BrandProject, MatchingGiftResponse, ValidateMatchingGiftResponses } from '../../../../shared/brands.model';
import { CustomValidators } from '../../../../shared/custom-validators';
import { RouterUrls } from '../../../../shared/router-urls';
import { TagsService } from 'src/app/services/tags.service';
import { MatchingGiftDialogComponent } from 'src/app/brands-module/matching-gift-dialog/matching-gift-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { NoKpiMatchingGiftDialogComponent } from 'src/app/brands-module/no-kpi-matching-gift-dialog/no-kpi-matching-gift-dialog.component';

@Component({
  selector: 'app-no-kpimatching-gift-page',
  templateUrl: './no-kpi-matching-gift-page.component.html',
  styleUrls: ['./no-kpi-matching-gift-page.component.scss']
})
export class NoKpiMatchingGiftPageComponent implements OnInit, OnDestroy {

  programInfo$: Observable<any>;
  programInfo;
  emailControl = new UntypedFormControl('', [Validators.required, Validators.pattern(CustomValidators.emailValidationRegex)] );
  chosenProject;
  clickEventSubscription: Subscription;
  isLoading = true;

  constructor(private languageService: LanguageService,
              private brandsService: BrandsService,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private meta: Meta,
              private tagsService: TagsService,
              private titleService: Title,
              private dialog: MatDialog,
              private sanitizer: DomSanitizer,
              private translate: TranslateService) {
    this.programInfo$ = activatedRoute.params
      .pipe(
        // Get matching gift info and save to variable
        switchMap(param => this.brandsService.getNoKpiMatchingGiftInfo(param.program)),
        tap(value => {
          this.programInfo = value;
          this.updateTags();
          this.isLoading = false;
        }),
        // Redirect to brand page if there is no project with such id;
        catchError((error) => {
          this.router.navigate(['../'], {relativeTo: this.activatedRoute});
          this.isLoading = false;
          return of(error);
        }),
      );
    this.clickEventSubscription = this.tagsService.getClickEventBrandProgram().subscribe(() => {
      this.updateTags();
    });
  }

  ngOnInit(): void {
    this.brandsService.isBrandPage.next(true);
  }

  updateTags() {
    this.titleService.setTitle(this.programInfo.title[this.currentLanguage]);
    this.meta.updateTag({name: 'description', content: this.programInfo.subtitle[this.currentLanguage]});
    this.meta.updateTag({property: 'og:title', content: this.programInfo.title[this.currentLanguage]});
    this.meta.updateTag({property: 'og:description', content: this.programInfo.subtitle[this.currentLanguage]});
    this.meta.updateTag({property: 'og:type', content: 'URL'});
    this.meta.updateTag({property: 'og:url', content: window.location.href});
    this.meta.updateTag({property: 'og:image', content: this.programInfo.picture_cover});
  }

  get currentLanguage() {
    return this.languageService.getUserUsedLanguage();
  }

  openPaymentModal(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '953px';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'no-kpi-matching-gift-dialog';
    dialogConfig.maxWidth = '953px';
    dialogConfig.closeOnNavigation = true;
    dialogConfig.data = {
      logo: this.programInfo.logo,
      color: this.programInfo.color,
      companyName: this.programInfo.company_name,
      projectId: this.chosenProject.no_kpi_project_id,
      matchingGiftId: this.programInfo.no_kpi_matching_gift_id,
      multiplicationFactor: this.programInfo.multiplication_factor,
      email: this.emailControl.value,
      premiumNumber: this.programInfo.premium_number,
      premiumFactor: this.programInfo.premium_factor,
      organizationName: this.chosenProject.organization,
      companySlug: this.programInfo.company_slug
    };
    dialogConfig.backdropClass = 'backdropBackground';
    this.dialog.open(NoKpiMatchingGiftDialogComponent, dialogConfig);
  }

  getRGBAColor(opacity) {
    const [r, g, b] = this.programInfo.color.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  getSvgImage() {
    return this.sanitizer.bypassSecurityTrustHtml(`<?xml version="1.0" encoding="UTF-8"?>
    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="57pt" height="56pt" viewBox="0 0 57 56" version="1.1">
    <g id="surface1">
    <path style=" stroke:none;fill-rule:evenodd;fill:${this.programInfo.color};fill-opacity:1;" d="M 28.511719 0 C 44.238281 0 57 12.546875 57 28 C 57 43.453125 44.238281 56 28.511719 56 C 12.761719 56 0 43.453125 0 28 C 0 12.546875 12.761719 0 28.511719 0 Z M 10.347656 38.632812 C 10.371094 38.632812 18.308594 38.636719 18.308594 38.636719 C 15.414062 35.96875 13.628906 32.1875 13.628906 28 C 13.628906 19.933594 20.285156 13.382812 28.511719 13.382812 C 36.714844 13.382812 43.371094 19.933594 43.371094 28 C 43.371094 32.1875 41.585938 35.96875 38.714844 38.636719 C 43.03125 38.636719 43.828125 38.632812 46.652344 38.632812 C 47.761719 38.632812 48.679688 39.523438 48.679688 40.625 C 48.679688 41.722656 47.761719 42.617188 46.652344 42.617188 L 10.347656 42.617188 C 9.238281 42.617188 8.320312 41.722656 8.320312 40.625 C 8.320312 39.527344 9.238281 38.632812 10.347656 38.632812 Z M 28.511719 17.40625 C 34.445312 17.40625 39.292969 22.152344 39.292969 28 C 39.292969 33.847656 34.445312 38.597656 28.511719 38.597656 C 22.554688 38.597656 17.730469 33.847656 17.730469 28 C 17.730469 22.152344 22.554688 17.40625 28.511719 17.40625 Z M 28.511719 17.40625 "/>
    </g>
    </svg>`);
  }

  chooseProject(project, target) {
    this.chosenProject = project;
    this.openPaymentModal();
  }

  getProjectUrl(projectSlug: string) {
    return `${this.languageService.getCurrentLangWithSlash()}/${RouterUrls.BrowseProjects}/${projectSlug}`;
  }

  ngOnDestroy(): void {
    this.brandsService.isBrandPage.next(false);
  }
}
